import { useMemo } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { calculateEstimatedReadTime, getAdf, getWordCount } from '../readTimeUtils.ts';
import type { ContentFeatures } from '../featuresExtractor.ts';
import { extractContentFeatures, extractContentFeaturesSkipExpand } from '../featuresExtractor.ts';

import type {
	useReadTimeQuery$data,
	useReadTimeQuery$variables,
} from './__generated__/useReadTimeQuery.graphql.ts';

export const ReadTimeQuery: GraphQLTaggedNode = graphql`
	query useReadTimeQuery($contentId: ID, $status: [String], $includeContentBody: Boolean = true) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				type
				body @include(if: $includeContentBody) {
					dynamic {
						value
						representation
					}
				}
				operations {
					operation
					targetType
				}
			}
		}
	}
`;

export const useReadTime = (
	contentId: string,
	skipQuery?: boolean,
): {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	adf: JSONDocNode | undefined;
	contentType: string | null | undefined;
	readTime: number | null;
	wordCount: number | null;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
} => {
	const readTimeQueryOptions = {
		variables: {
			contentId,
		},
		skip: skipQuery,
	};

	const { data, loading, error } = useQueryNoFurtherUpdateInterop<
		useReadTimeQuery$data,
		useReadTimeQuery$variables
	>(ReadTimeQuery, readTimeQueryOptions);

	const { adf, readTime, wordCount, features, featuresSkipExpand } = useMemo(() => {
		const adfFromData = getAdf(data ?? undefined);

		if (!adfFromData || loading || error || !contentId) {
			return {
				readTime: null,
				wordCount: null,
				features: null,
				featuresSkipExpand: null,
			};
		}

		return {
			adf: adfFromData,
			readTime: calculateEstimatedReadTime(adfFromData),
			wordCount: getWordCount(adfFromData),
			features: extractContentFeatures(adfFromData),
			featuresSkipExpand: extractContentFeaturesSkipExpand(adfFromData),
		};
	}, [loading, error, data, contentId]);

	const contentNode = data?.content?.nodes?.[0];
	const contentType = contentNode?.type;

	return {
		loading,
		error,
		adf,
		contentType,
		readTime,
		wordCount,
		features,
		featuresSkipExpand,
	};
};
